import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Heart, Hammer, GraduationCap, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const PERKS = [
  { icon: Lock, t: "Premium Spaces", d: "Unlock members-only spaces where the deeper conversations happen." },
  { icon: GraduationCap, t: "Every paid course", d: "All premium courses included — no separate purchases." },
  { icon: Hammer, t: "The Build studio", d: "Start & join projects with live code, whiteboards and chat." },
];

export default function Upgrade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("annual");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => { api.get("/billing/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);

  const isPro = user?.plan === "pro";
  const paymentsEnabled = status?.enabled;

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <PageHeader eyebrow="Ikigora Pro" title="Support Ikigora. Unlock everything."
        subtitle="A small, symbolic contribution — not to make anyone rich, but to keep the community growing and the platform evolving." />

      {isPro ? (
        <div className="border border-cobalt/40 bg-secondary/40 rounded-xl p-8 text-center" data-testid="already-pro">
          <Sparkles size={30} className="mx-auto text-cobalt mb-3" />
          <h2 className="font-serif text-2xl font-bold">You're an Ikigora Pro member 💙</h2>
          {status?.founding_member && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-foreground bg-gold rounded-full px-3 py-1" data-testid="founding-status">
              ★ Founding member{status.founding_number ? ` #${status.founding_number}` : ""}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-2">Thank you for helping the community thrive. Everything is unlocked.</p>
          <button onClick={() => navigate("/feed")} className="mt-5 bg-cobalt text-white rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gold-hover transition-colors">
            Back to the community
          </button>
        </div>
      ) : (
        <>
          {paymentsEnabled === false && (
            <div className="border border-gold/40 bg-gold/10 rounded-xl px-5 py-3.5 flex items-center gap-3" data-testid="payments-coming-soon">
              <Sparkles size={16} className="text-gold shrink-0" />
              <p className="text-sm text-foreground"><span className="font-semibold">Payments are coming soon.</span> Pro isn't purchasable in this early beta yet — you can still explore what it unlocks below.</p>
            </div>
          )}
          <div className="border border-border bg-card rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-6">
              <Heart size={20} className="text-cobalt mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                Ikigora is built by a tiny team who care about this community. Pro is our way of keeping the lights on and shipping new things —
                think of it as <span className="text-foreground font-medium">chipping in</span>, not a paywall to get rich. If it's ever a burden, the core community stays free.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {PERKS.map((p) => (
                <div key={p.t} className="border border-border rounded-lg p-4">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary text-cobalt mb-3"><p.icon size={17} /></span>
                  <div className="text-sm font-semibold">{p.t}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{p.d}</div>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={() => setPlan("annual")} data-testid="plan-annual"
                className={`text-left rounded-xl border-2 p-5 transition-colors ${plan === "annual" ? "border-cobalt bg-secondary/40" : "border-border hover:border-cobalt/50"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Annual</span>
                  <span className="text-[10px] font-mono uppercase tracking-wider bg-cobalt text-white rounded-full px-2 py-0.5">Best value · 2 months free</span>
                </div>
                <div className="mt-2"><span className="font-serif text-3xl font-bold">$99</span><span className="text-sm text-muted-foreground">/year</span></div>
                <div className="text-xs text-muted-foreground mt-1">≈ 899 SEK · about $8.25/mo</div>
              </button>
              <button onClick={() => setPlan("monthly")} data-testid="plan-monthly"
                className={`text-left rounded-xl border-2 p-5 transition-colors ${plan === "monthly" ? "border-cobalt bg-secondary/40" : "border-border hover:border-cobalt/50"}`}>
                <div className="font-semibold">Monthly</div>
                <div className="mt-2"><span className="font-serif text-3xl font-bold">$9</span><span className="text-sm text-muted-foreground">/month</span></div>
                <div className="text-xs text-muted-foreground mt-1">≈ 89 SEK · cancel anytime</div>
              </button>
            </div>

            <button onClick={subscribe} disabled={busy || paymentsEnabled === false} data-testid="upgrade-checkout-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-cobalt text-white rounded-md px-6 py-3.5 text-sm font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Sparkles size={16} /> {paymentsEnabled === false ? "Payments coming soon" : busy ? "Redirecting…" : `Become a Pro — ${plan === "annual" ? "$99/year" : "$9/month"}`}
            </button>
            <p className="text-center text-xs text-muted-foreground mt-3 flex items-center justify-center gap-1.5">
              <Check size={12} /> Secure checkout via Stripe · cancel anytime · early supporters get a ★ Founding badge
            </p>
          </div>
        </>
      )}
    </div>
  );
}
